/**
 * Which floor a link to a lift lands on (twin slice 1, plan W4 step 6;
 * catalogue §8: "the floor of the robot that owns or waits on the
 * session, else `current_level`, else the whole building").
 *
 * A link to SL1 is almost always about a robot and SL1 together: the
 * robot waiting at a hall, or held in the car. Landing on the car's own
 * `current_level` would put the person on the floor the car happens to
 * stand at, not where the trouble is. So:
 *
 *   1. THE ROBOT THAT OWNS OR WAITS ON THE CAR, first match wins:
 *        - the adapter's robot owner of an ACTIVE session (`session.owner`,
 *          `<fleet>/<robot>`, the same id "End RMF session" keys on);
 *        - the RMF session the car itself echoes (`rmfSessionId`);
 *        - the supervisor's standing non-END request (`standingRequest`),
 *          i.e. a boarding in progress.
 *      Each only counts when it names a robot in the roster: an id the
 *      console cannot find is somebody else's session (the oracle
 *      dashboard's, a stuck fleet adapter's) and says nothing about a
 *      floor.
 *   2. THAT ROBOT'S FLOOR. Inside the car -- `cabinClearanceM === 0`,
 *      through robot-presence's own `isInsideCar` -- it is the CAR's
 *      floor: the robot's pose still names the floor it boarded on. A
 *      robot waiting on the landing, even with its nose over the sill
 *      (within the 0.225 m release clearance `heldLift` uses), is NOT
 *      inside and lands on its own floor (parity with W3's riding
 *      predicate, conflict X33). Its own floor is where presence draws it
 *      (the live pose, or the ghost when lost), or presence's floor row
 *      when it cannot be placed. A lost robot whose ghost stands inside
 *      a car has no floor of its own (the pose names where it boarded):
 *      inside THIS car it is the car's floor, as for a rider; inside
 *      another, the car's floor as if no robot could be placed.
 *   3. Else the car's `current_level`.
 *   4. Else nowhere: the whole building. A car is never placed between
 *      floors, so an unreported floor is not guessed.
 */
import type { LiftViewModel } from '../api/adapters';
import type { RobotStateView } from '../api/rmf-client';
import { robotReceiptKey } from '../api/robot-receipts';
import { isInsideCar, type RobotPresence } from '../screens/Map/robot-presence';
import { END_SESSION_REQUEST_TYPE } from '../screens/Tasks/task-ownership';

export type LandingLift = Pick<
  LiftViewModel,
  | 'name'
  | 'levels'
  | 'currentLevel'
  | 'ref'
  | 'refYaw'
  | 'width'
  | 'depth'
  | 'session'
  | 'rmfSessionId'
  | 'standingRequest'
>;

export type LandingRobot = Pick<RobotStateView, 'name' | 'fleet' | 'location'>;

export type LiftLandingBasis =
  /** The session's robot is inside the car: the car's floor. */
  | 'robot-in-car'
  /** The session's robot is not inside the car: its own floor. */
  | 'robot-floor'
  /** No session robot could be placed: the car's reported floor. */
  | 'car-floor'
  /** Nothing to go on: the whole building. */
  | 'none';

export interface LiftLanding {
  /** The floor to land on, or null for the whole building. */
  readonly level: string | null;
  readonly basis: LiftLandingBasis;
  /** `<fleet>/<robot>` of the robot the landing followed, if any. */
  readonly robotKey: string | null;
}

/**
 * Every robot id the car's session state names, in the order above,
 * before any roster check: the adapter owner of an active session, the
 * car's RMF echo, the standing non-END request. A caller that has no
 * roster yet uses this to know whether waiting for one could change the
 * answer.
 */
export function sessionRobotIds(lift: LandingLift): string[] {
  const out: string[] = [];
  const push = (id: string | null | undefined) => {
    if (id && !out.includes(id)) out.push(id);
  };
  const session = lift.session;
  if (session?.active === true && session.owner?.kind === 'robot') push(session.owner.id);
  push(lift.rmfSessionId);
  const standing = lift.standingRequest;
  if (standing && standing.request_type !== END_SESSION_REQUEST_TYPE) push(standing.session_id);
  return out;
}

/** A pose a robot really reported: a level, and not the (0, 0) LOST sentinel. */
function realLocation(robot: LandingRobot): { map: string; x: number; y: number } | null {
  const loc = robot.location;
  if (!loc || !loc.map) return null;
  if (loc.x === 0 && loc.y === 0) return null;
  return loc;
}

/** Where presence draws the robot, or its "?" row; the record's own level
 *  when there is no presence for it. None for a lost robot whose ghost
 *  stands inside a car: that pose names the floor it BOARDED on. */
function ownFloor(robot: LandingRobot, presence: RobotPresence | undefined): string | null {
  if (presence?.kind === 'lost' && presence.liftName) return null;
  if (presence) return presence.pose?.map || presence.level || null;
  return realLocation(robot)?.map ?? null;
}

/** The car's reported floor, or null. */
function carFloor(lift: LandingLift): string | null {
  return lift.currentLevel ? lift.currentLevel : null;
}

/** Pure: which floor a link to `lift` lands on. */
export function liftLandingLevel(
  lift: LandingLift,
  robots: readonly LandingRobot[] | undefined,
  presence?: ReadonlyMap<string, RobotPresence>,
): LiftLanding {
  const byKey = new Map((robots ?? []).map((r) => [robotReceiptKey(r.fleet, r.name), r]));
  for (const id of sessionRobotIds(lift)) {
    const robot = byKey.get(id);
    if (!robot) continue;
    const p = presence?.get(id);
    const loc = realLocation(robot);
    // Riding THIS car (presence) or inside it by the cabin check: the
    // same predicate either way, `cabinClearanceM === 0`. A lost robot
    // whose ghost stands in THIS car (a silent rider, often a worker stub
    // with no pose of its own) was last seen inside it: the car decides,
    // never the floor that ghost's pose names, where it boarded.
    const inside =
      ((p?.kind === 'riding' || p?.kind === 'lost') && p.liftName === lift.name) ||
      (loc !== null && isInsideCar(lift, loc));
    if (inside) {
      const level = carFloor(lift);
      return level
        ? { level, basis: 'robot-in-car', robotKey: id }
        : { level: null, basis: 'none', robotKey: id };
    }
    const level = ownFloor(robot, p);
    if (level) return { level, basis: 'robot-floor', robotKey: id };
    // The car's robot cannot be placed: the car itself is the answer.
    return fromCar(lift, id);
  }
  return fromCar(lift, null);
}

function fromCar(lift: LandingLift, robotKey: string | null): LiftLanding {
  const level = carFloor(lift);
  return level ? { level, basis: 'car-floor', robotKey } : { level: null, basis: 'none', robotKey };
}
